import { useContext, useState } from 'react';
import axios from 'axios';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import ButtonOutline from './ButtonOutline';
import LoadingAnimation from './LoadingAnimation';
import PopupWithClose from './PopupWithClose';
import AssetList from './AssetList';

// Main assembly of an order.
//  mode "choose" (create order): pick ONE of the template's main assemblies (radio list).
//  mode "edit" (edit order): shows the order's main assembly (its first assembly), locked,
//  with a button to replace it.
function OrderMainAssembly({ loading, mode, options = [], selectedId, onSelect, main, changing, onToggleChange }) {
    const { token, logout } = useContext(AuthContext);
    const [filesPopup, setFilesPopup] = useState(false);
    const [fetchingAssets, setFetchingAssets] = useState(true);
    const [assetsData, setAssetsData] = useState([]);

    const showDrawing = async (assets) => {
        setFilesPopup(true);
        if (!assets || assets.length === 0) {
            setAssetsData([]);
            setFetchingAssets(false);
            return;
        }
        try {
            setFetchingAssets(true);
            const response = await axios.get(`${baseURL}/asset/getAssets`, {
                headers: {
                    'ngrok-skip-browser-warning': 'any',
                    'Content-Type': 'multipart/form-data',
                    'x-access-token': token,
                },
                params: { asset_ids: assets },
            });
            setAssetsData(response.data.data.assetData);
        } catch (error) {
            console.error('Error:', error);
            if (error.response?.status === 401) {
                logout();
            }
        } finally {
            setFetchingAssets(false);
        }
    };

    const details = (assembly) => (
        <div className='min-w-0'>
            <p className='text-lg font-medium mb-1'>{`Assembly# - ${assembly.assembly_number}`}</p>
            <p className='text-gray-600 mb-2'>{assembly.assembly_description}</p>
            <div className='px-4 border border-gray-500 w-fit rounded-full text-gray-500'>{assembly.assembly_type}</div>
        </div>
    );

    const drawingLink = (assembly) => (
        <span
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); showDrawing(assembly.assets); }}
            className='cursor-pointer border-b border-gray-600 hover:border-orange-600'
        >
            Drawing
        </span>
    );

    const required = mode === 'choose' || Boolean(main);

    return (
        <div id='OrderMainAssembly' className='bg-white shadow-md rounded-lg mb-5 w-full py-4 px-6'>
            <div className='flex flex-wrap items-center justify-between gap-2 mb-3'>
                <p className='text-2xl font-semibold'>
                    Main Assembly {required && <span className='text-red-600'>*</span>}
                </p>
                {mode === 'edit' && !loading && (
                    <ButtonOutline
                        buttonText={changing ? 'Cancel Change' : main ? 'Change Main Assembly' : 'Select Main Assembly'}
                        onClick={onToggleChange}
                    />
                )}
            </div>

            {loading ? (
                <LoadingAnimation />
            ) : mode === 'choose' ? (
                options.length === 0 ? (
                    <p className='text-gray-500'>This template has no main assembly.</p>
                ) : (
                    <>
                        {options.length > 1 && (
                            <p className='text-sm text-gray-500 mb-3'>
                                This template has more than one main assembly. Choose one for this order.
                            </p>
                        )}
                        <div className='flex flex-col gap-3'>
                            {options.map((option) => (
                                <label
                                    key={option._id}
                                    data-main-option={option.assembly_number}
                                    className={`border rounded-lg p-4 flex items-start gap-3 ${options.length > 1 ? 'cursor-pointer' : 'cursor-default'} ${
                                        selectedId === option._id ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                                    }`}
                                >
                                    <input
                                        type='radio'
                                        name='order_main_assembly'
                                        checked={selectedId === option._id}
                                        disabled={options.length === 1}
                                        onChange={() => onSelect(option._id)}
                                        className='mt-1 h-5 w-5 accent-orange-500'
                                    />
                                    <div className='flex flex-1 flex-wrap items-start justify-between gap-3'>
                                        {details(option)}
                                        {drawingLink(option)}
                                    </div>
                                </label>
                            ))}
                        </div>
                    </>
                )
            ) : main ? (
                <div
                    data-main-option={main.assembly_number}
                    className={`border rounded-lg p-4 flex flex-wrap items-start justify-between gap-3 cursor-default ${
                        changing ? 'border-orange-400' : 'border-gray-300'
                    }`}
                >
                    {details(main)}
                    {drawingLink(main)}
                </div>
            ) : (
                <div className='border border-dashed border-gray-300 rounded-lg p-4'>
                    <p className='text-gray-500'>
                        This order has no main assembly. Select one from the assembly list.
                    </p>
                </div>
            )}

            {mode === 'edit' && changing && (
                <p className='text-sm text-gray-500 mt-2'>
                    Choose the new main assembly from the list. The current main assembly will be replaced.
                </p>
            )}

            {filesPopup && (
                <PopupWithClose closePopup={setFilesPopup}>
                    <AssetList data={assetsData} fetchingAssets={fetchingAssets} />
                </PopupWithClose>
            )}
        </div>
    );
}

export default OrderMainAssembly;
